import { ReviewType } from '@shared/react-ui-core';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { PropsWithChildren } from 'react';

const parseTag = (tag: CustomTag) => {
  if (tag?.__typename === 'DatoCmsSeoMetaTagContent') {
    const { name, content } = tag;
    return [
      {
        tagName: 'meta',
        attributes: { name, content },
      },
    ];
  }

  return [];
};

const Seo: React.FC<PropsWithChildren & { seo?: SeoFragment }> = ({
  seo,
  children,
}) => {
  const {
    seoMetaTags = null,
    seoRichStructuredData = null,
    canonicalLink = null,
    customTags = null,
  } = seo || {};

  const tags = [
    ...(seoMetaTags?.tags?.length ? seoMetaTags?.tags : []),
    ...(canonicalLink
      ? [
          {
            tagName: 'link',
            attributes: { rel: 'canonical', href: `${canonicalLink}` },
          },
        ]
      : []),
    ...(customTags?.length
      ? customTags.reduce(
          (agg: ReturnType<typeof parseTag> = [], customTag) => [
            ...agg,
            ...parseTag(customTag),
          ],
          [],
        )
      : []),
  ] as const;

  return (
    <>
      {tags.map((tag, index) =>
        React.createElement(
          tag.tagName,
          { key: `tag-${tag.tagName}-${index}`, ...tag.attributes },
          tag.content,
        ),
      )}
      {seoRichStructuredData && (
        <script key={'seoRichStructuredData'} type="application/ld+json">
          {typeof seoRichStructuredData === 'string'
            ? seoRichStructuredData
            : JSON.stringify(seoRichStructuredData)}
        </script>
      )}
      {children}
    </>
  );
};

Seo.propTypes = {
  seo: PropTypes.shape({
    seoMetaTags: PropTypes.shape({
      tags: PropTypes.arrayOf(
        PropTypes.shape({
          tagName: PropTypes.string,
          content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          attributes: PropTypes.shape({
            property: PropTypes.string,
            content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          }),
        }),
      ),
    }),
    seoRichStructuredData: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
    ]),
  }),
  children: PropTypes.arrayOf(PropTypes.element),
};

Seo.defaultProps = {
  seo: null,
};

export default Seo;

export interface CustomTag {
  __typename: 'DatoCmsSeoMetaTagContent';
  id: string;
  name: string;
  content: string;
}

export interface SeoFragment {
  seoMetaTags?: {
    tags?:
      | {
          tag: string;
          content?: string;
          attributes?: { name?: string; property: string; content: string };
        }[]
      | null;
  } | null;
  seoRichStructuredData?: string | null;
  canonicalLink?: string | null;
  customTags?: CustomTag[] | null;
  fadeslideshowreviews:
    | {
        id: string;
        customerName: string;
        customerReview: string;
        reviewDate: string;
      }[]
    | null;
  images: ImageObject[] | null;
  videos:
    | {
        name: string;
        video: {
          video: {
            url: string;
            thumbnail: string;
            [key: string]: string;
          };
        };
      }[]
    | null;
  reviews: ReviewType[];
  heroImage: ImageType | null;
  heroSubtext: string | null;
  heroText: string | null;
  hideBanner: boolean | null;
  navLogo: ImageType | null;
}

export type ImageObject = {
  id: string;
  name: string;
  image: ImageType;
};

export type ImageType = {
  id: string;
  url: string;
  alt: string;
  customData?: Record<string, string>;
  title: string | null;
};

export interface SeoFragmentPage {
  datoCmsSeo: SeoFragment;
  datoCmsSiteMetaInfo: {
    promoBannerSaleName: string;
    promoBannerTitle: string;
    promoBannerBoldText: string;
    promoBannerText: string;
    promoSaleEndDate: string;
    google: number;
    bestCompany: number;
    trustpilot: number;
    numberOfReviewsBestCompany: number;
    numberOfReviewsGoogle: number;
    numberOfReviewsTrustpilot: number;
    tyvmFreeEquipmentCap: number;
    universalDiscount: number;
    panelDiscount: number;
  };
}

export const seoFragment = graphql`
  fragment SeoFragmentPage on Query {
    datoCmsSiteMetaInfo(locale: $language) {
      promoBannerSaleName
      promoBannerTitle
      promoBannerBoldText
      promoBannerText
      promoSaleEndDate
      google
      bestCompany
      trustpilot
      numberOfReviewsBestCompany
      numberOfReviewsGoogle
      numberOfReviewsTrustpilot
      tyvmFreeEquipmentCap
      benefitsFreeEquipmentCap
      universalDiscount
      panelDiscount
      promoSaleStartDate
      promoSaleEndDate
    }

    datoCmsSeo(path: { eq: $slug }, locale: $language) {
      ...SeoFragment
      images {
        id
        name
        image {
          url
          alt
          title
        }
      }
      fadeslideshowreviews {
        id
        customerName
        customerReview
        reviewDate
      }
      reviews {
        profileImage {
          url
          alt
        }
        reviewContent
        reviewDate
        reviewerName
        customData
        id
      }
    }
  }
  fragment SeoFragment on DatoCmsSeo {
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seoRichStructuredData
    canonicalLink
    customTags {
      __typename
      id
      name
      content
    }
    images {
      id
      name
      image {
        url
        alt
        title
        customData
      }
    }
  }
`;
